/* eslint-disable no-console */
import {Component, Mixins, Watch} from 'vue-property-decorator';
import {MetaMixin} from '../../mixins/meta.mixin';
import {RouteName} from '../routes.constants';
import {magic} from '@gig/magic-sdk';
import {
    USER_LOGGED_IN_QUERY,
    WINDOW_ROUTE_CHANGE,
    WINDOW_BEFORE_UNLOAD,
    USER_LOGGED_IN_UPDATED
} from '@gig/magic-messages';
import type {ISubscription} from '@gig/magic-message-bus/dist/base-bus';
import {config} from '../../services/config';


enum PostMethods {
    LOGIN = 'LOGIN',
    LOGOUT = 'LOGOUT',
    PAGE_NAVIGATION = 'PAGE_NAVIGATION',
}

interface IPostMethodsArguments {
    url?: string;
    sessionId?: number;
    targetPage?: string;
}

export const JACKPOTS_READY = 'JACKPOTS_READY';
export const JACKPOTS = 'jackpots';
export const JACKPOTS_URL = config.jackpots?.origin || 'https://jackpots-test.thepools.com';

@Component({
    name: 'JackpotsRoute'
})
export default class JackpotsRoute extends Mixins(MetaMixin) {
    url: string = '';
    isIframeReady: boolean = false;
    iframeHeight: string = '';
    iframeOrigin: string = '';
    iframeOffset = 100;
    private isInitialized: boolean = false;
    iframeKey: string = 'logged-out';
    private lastIframeNavigationPath: string = '';

    private waitingForIframeNavigation: boolean = false;
    private lastConfirmedIframePage: string = '';
    private sessionId = '';
    private userId: string | number | undefined;
    private routeChangeSubscription: ISubscription | undefined;
    private userLoggedInSubscription: ISubscription | null = null;
    private windowUnloadSubscription: ISubscription | undefined;

    private isJackpotsRouteActive(routeName: string | null | undefined = this.$route.name) {
        return routeName === RouteName.JACKPOTS || routeName === RouteName.HOME;
    }

    async created() {
        const isLoggedIn = await this.isLoggedIn();
        this.iframeKey = isLoggedIn ? 'logged-in' : 'logged-out';
        if (!this.isInitialized) {
            const redirectUrl = sessionStorage.getItem('redirectUrl')
            sessionStorage.removeItem('redirectUrl');
            redirectUrl && this.$router.push(redirectUrl);

            this.url = JACKPOTS_URL;
            this.iframeOrigin = this.url;

            window.addEventListener('message', this.handleIframeMessage);
            this.isInitialized = true;
        }

        const metaUrl = this.getMetaUrl(this.$route.params?.jackpots);
        this.setPage(`jackpots`, {slug: metaUrl});
    }

    async mounted() {
        const earlyIframe = document.getElementById('early-jackpot-iframe') as HTMLIFrameElement;
        const earlyContainer = document.getElementById('early-jackpot-container') as HTMLDivElement;

        earlyContainer.style.position = 'relative';
        earlyContainer.style.top = '';
        earlyContainer.style.left = '';
        earlyContainer.style.width = '100%';
        earlyContainer.style.height = '100vh';
        earlyContainer.style.minHeight = '200px';
        earlyContainer.style.zIndex = '1';

        const revealIframe = () => {
            earlyContainer.style.visibility = 'visible';
            earlyContainer.style.opacity = '1';
            earlyContainer.style.pointerEvents = 'auto';
            this.isIframeReady = true;

            this.iframeLogin();
        };


        earlyIframe.onload = () => {
            revealIframe();
        };

        setTimeout(() => {
            if (!this.isIframeReady) {
                revealIframe();
            }
        }, 100);

        (this.$refs as any).jackpot = earlyIframe;

        this.setupSubscriptions();

        if (this.$route.name !== RouteName.JACKPOTS && this.$route.name !== 'home') {
            earlyContainer.style.display = 'none';
            earlyContainer.style.visibility = 'hidden';
            earlyContainer.style.opacity = '0';
            earlyContainer.style.pointerEvents = 'none';
        }
    }

    private setupSubscriptions() {
        // Subscribe to login state changes
        this.userLoggedInSubscription = window.eventBus.subscribe(USER_LOGGED_IN_UPDATED, async () => {
            await this.iframeLogin();
        });
        // Subscribe to window unload
        this.windowUnloadSubscription = window.eventBus.subscribe(WINDOW_BEFORE_UNLOAD, () => {
            this.postMessageMethod(PostMethods.LOGOUT);
        });
    }

    private updateContainerHeight(height: number) {
        const earlyContainer = document.getElementById('early-jackpot-container') as HTMLDivElement;
        if (earlyContainer) {
            earlyContainer.style.height = `${height + this.iframeOffset}px`;
        }
    }

    destroyed() {
        window.removeEventListener('message', this.handleIframeMessage);
        this.routeChangeSubscription?.unsubscribe();
        this.windowUnloadSubscription?.unsubscribe();
        this.userLoggedInSubscription?.unsubscribe();
    }

    private async handleIframeMessage(event: MessageEvent) {
        if (!this.isJackpotEvent(event)) {
            console.warn(
                'Origin mismatch or unauthorized source:',
                event.origin
            );
            return;
        }

        const data = event.data;
        if (data.action === 'IFRAME_LOADED') {
            this.isIframeReady = true;
            const currentUrl = window.location.pathname;

            /*
                relativePath for '/jackpots/winners' = winners
                relativePath for '/jackpots/' = ''
                trailing '/' can be removed or added after jackpots
            */
            const relativePath = this.$route.params?.jackpots ?? ''

            if (relativePath.length > 0) {
                this.postMessageMethod(PostMethods.PAGE_NAVIGATION, {
                    targetPage: currentUrl.replace(
                        '/jackpots/',
                        ''
                    ),
                });
            } else {
                this.postMessageMethod(PostMethods.PAGE_NAVIGATION, {
                    targetPage: '/',
                });
            }

            if (data.additionalData?.height) {
                const height = data.additionalData.height;
                this.iframeHeight = `${height}px`;
                this.updateContainerHeight(height);
                this.windowUnloadSubscription = window.eventBus.subscribe(
                    WINDOW_BEFORE_UNLOAD,
                    () => {
                        this.iframeLogout();
                    }
                );
                this.userLoggedInSubscription = window.eventBus.subscribe<
                    boolean
                >(
                    USER_LOGGED_IN_UPDATED,
                    async (event: any) => {
                        const newKey = event.payload ? 'logged-in' : 'logged-out';
                        if (this.iframeKey !== newKey) {
                            this.iframeKey = newKey;
                        }
                    },
                    {replay: true}
                );

                window.eventBus.publish(JACKPOTS_READY, true);
                await this.iframeLogin();
            }
        } else if (
            data.action === 'IFRAME_RESIZE' &&
            data.additionalData?.height
        ) {
            const height = data.additionalData.height;
            this.iframeHeight = `${height}px`;
            this.updateContainerHeight(height);
        } else if (
            data.action === 'PAGE_NAVIGATION' &&
            data.additionalData.targetPage
        ) {
            const targetPage = data.additionalData.targetPage;

            this.lastConfirmedIframePage = targetPage;

            if (!this.isJackpotsRouteActive()) {
                return;
            }

            if (data.additionalData?.height) {
                const height = data.additionalData.height;
                this.iframeHeight = `${height}px`;
                this.updateContainerHeight(height);
            }

            const currentUrl = window.location.pathname;
            const targetPath = `/jackpots${targetPage}`;

            if (currentUrl !== targetPath) {
                this.lastIframeNavigationPath = targetPath;
                this.$router.replace(targetPath);
            }

            window.eventBus.publish(WINDOW_ROUTE_CHANGE, targetPath);

            const metaUrl = this.getMetaUrl(targetPage.substring(1))
            this.setPage(`jackpots`, {slug: metaUrl});

            const earlyContainer = document.getElementById('early-jackpot-container') as HTMLDivElement;
            if (earlyContainer && this.isJackpotsRouteActive()) {
                this.waitingForIframeNavigation = false;
                earlyContainer.style.display = 'block';
                earlyContainer.style.visibility = 'visible';
                earlyContainer.style.opacity = '1';
                earlyContainer.style.pointerEvents = 'auto';
            }

        } else if (data.action === 'REDIRECTION' && data.additionalData.feature) {
            const feature = data.additionalData.feature;
            const redirectUrl = data.additionalData.redirectUrl
            sessionStorage.setItem('redirectUrl', `${redirectUrl}`);

            if (data.additionalData?.height) {
                const height = data.additionalData.height;
                this.iframeHeight = `${height}px`;
                this.updateContainerHeight(height);
            }

            if (await this.isLoggedIn()) {
                return;
            }
            if (feature === 'registration') {
                this.$router.push({
                    name: RouteName.SIGN_UP,
                    replace: true
                });
            } else if (feature === 'login') {
                this.$router.push({
                    name: RouteName.LOGIN,
                    query: {
                        backTo: redirectUrl,
                    },
                });
            }
        }
    }

    private isJackpotEvent(event: MessageEvent) {
        return event.origin === this.iframeOrigin;
    }

    private getMetaUrl(location: string): string {
        if (location) {
            return location.replaceAll('/', '.')
        }
        return ``;
    }

    private isLoggedIn() {
        return window.queryBus.query<boolean>(USER_LOGGED_IN_QUERY);
    }

    private async iframeLogin() {
        const isLoggedIn = await this.isLoggedIn();
        if (isLoggedIn && this.isIframeReady) {
            this.sessionId = await magic.methods.externalServices.getSessionHash();
            const user = magic.session.getUser();
            this.userId = user?.userId;
            this.postMessageMethod(PostMethods.LOGIN);
        }
    }

    private iframeLogout() {
        if (!this.sessionId) {
            return;
        }

        this.postMessageMethod(PostMethods.LOGOUT);
        this.sessionId = '';
        this.userId = undefined;
    }

    private postMessageMethod(
        method: PostMethods,
        args?: IPostMethodsArguments
    ): void {
        const json = {
            action: method,
            additionalData: {
                playerId: this.userId,
                sessionHash: this.sessionId,
                ...args,
            },
        };

        const jackpots = this.$refs.jackpot as HTMLIFrameElement;
        jackpots.contentWindow!.postMessage(json, this.iframeOrigin);
    }

    @Watch('$route.fullPath')
    onRouteChanged() {
        if (!this.isJackpotsRouteActive()) {
            return;
        }

        if (this.waitingForIframeNavigation) {
            return;
        }

        if (this.lastIframeNavigationPath === this.$route.fullPath) {
            this.lastIframeNavigationPath = '';
            return;
        }

        const jackpotsSlug = this.$route.params?.jackpots;

        const metaUrl = this.getMetaUrl(jackpotsSlug || '');
        this.setPage('jackpots', { slug: metaUrl });

        this.postMessageMethod(PostMethods.PAGE_NAVIGATION, {
            targetPage: jackpotsSlug ? jackpotsSlug : '/',
        });
    }

    @Watch('$route.name')
    async onRouteNameChanged(newName: string, oldName: string) {
        if (this.isJackpotsRouteActive(oldName) && this.isJackpotsRouteActive(newName)) {
            return;
        }
        const earlyContainer = document.getElementById('early-jackpot-container') as HTMLDivElement;
        if (earlyContainer) {
            if (this.isJackpotsRouteActive(newName)) {
                const jackpotsSlug = this.$route.params?.jackpots;
                const expectedPage = jackpotsSlug ? `/${jackpotsSlug}` : '/';

                if (this.lastConfirmedIframePage === expectedPage) {
                    earlyContainer.style.display = 'block';
                    earlyContainer.style.visibility = 'visible';
                    earlyContainer.style.opacity = '1';
                    earlyContainer.style.pointerEvents = 'auto';
                    this.waitingForIframeNavigation = false;
                } else {
                    this.waitingForIframeNavigation = true;
                    earlyContainer.style.display = 'block';
                    earlyContainer.style.visibility = 'hidden';
                    earlyContainer.style.opacity = '0';
                    earlyContainer.style.pointerEvents = 'none';

                    this.postMessageMethod(PostMethods.PAGE_NAVIGATION, {
                        targetPage: jackpotsSlug ? jackpotsSlug : '/',
                    });

                    setTimeout(() => {
                        if (earlyContainer && this.isJackpotsRouteActive() && this.waitingForIframeNavigation) {
                            this.waitingForIframeNavigation = false;
                            earlyContainer.style.visibility = 'visible';
                            earlyContainer.style.opacity = '1';
                            earlyContainer.style.pointerEvents = 'auto';
                        }
                    }, 3000);
                }
            } else {
                earlyContainer.style.display = 'none';
                earlyContainer.style.visibility = 'hidden';
                earlyContainer.style.opacity = '0';
                earlyContainer.style.pointerEvents = 'none';
                this.postMessageMethod(PostMethods.PAGE_NAVIGATION, {
                    targetPage: '/',
                });
            }
        }

        if (newName === RouteName.JACKPOTS && oldName && oldName !== RouteName.JACKPOTS) {
            await this.iframeLogin();
        }
    }
}
